import React, { useState, useMemo } from 'react';
import AnalyticsButton from './AnalyticsButton';
import AnalyticsPanel from './AnalyticsPanel';
import { calculateDealsAnalytics } from '../../utils/analytics/dealsAnalytics';
import { calculateCompaniesAnalytics } from '../../utils/analytics/companiesAnalytics';
import { calculateInvestorsAnalytics } from '../../utils/analytics/investorsAnalytics';
import { calculateDealsNLP, calculateCompaniesNLP, calculateInvestorsNLP } from '../../utils/analytics/nlpAnalytics';
import './ContextualAnalytics.css';

function ContextualAnalytics({ activeTab, filteredData, allData }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate analytics based on current tab and filtered data
  const analyticsData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { summary: { total: 0, cards: [] }, charts: [] };
    }

    const options = { expanded: isExpanded };
    const currentAllData = allData?.[activeTab] || filteredData;

    switch (activeTab) {
      case 'deals':
        return calculateDealsAnalytics(filteredData, currentAllData, options);

      case 'companies':
        return calculateCompaniesAnalytics(filteredData, currentAllData, options);

      case 'investors':
        return calculateInvestorsAnalytics(filteredData, currentAllData, options);

      default:
        return { summary: { total: 0, cards: [] }, charts: [] };
    }
  }, [activeTab, filteredData, allData, isExpanded]);

  // Calculate NLP analytics for CURRENT tab
  const nlpData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) {
      return { wordClouds: [], keywordTrends: { trends: [], keywords: [] }, topicDistribution: [] };
    }

    switch (activeTab) {
      case 'deals':
        return calculateDealsNLP(filteredData);

      case 'companies':
        return calculateCompaniesNLP(filteredData);

      case 'investors':
        return calculateInvestorsNLP(filteredData);

      default:
        return { wordClouds: [], keywordTrends: { trends: [], keywords: [] }, topicDistribution: [] };
    }
  }, [activeTab, filteredData]);

  // Pre-calculate NLP data for ALL tabs (for background preloading)
  const allNLPData = useMemo(() => {
    if (!allData) return {};

    return {
      deals: allData.deals?.length > 0 ? calculateDealsNLP(allData.deals) : null,
      companies: allData.companies?.length > 0 ? calculateCompaniesNLP(allData.companies) : null,
      investors: allData.investors?.length > 0 ? calculateInvestorsNLP(allData.investors) : null
    };
  }, [allData]); // Only recalculate when allData changes (not on every filter)

  const insightCount = analyticsData.charts.length;

  // Don't show button if no data
  if (!filteredData || filteredData.length === 0) {
    return null;
  }

  return (
    <>
      <AnalyticsButton
        onClick={() => setIsOpen(true)}
        insightCount={insightCount}
        isOpen={isOpen}
      />
      {isOpen && (
        <AnalyticsPanel
          activeTab={activeTab}
          charts={analyticsData.charts}
          summary={analyticsData.summary}
          nlpData={nlpData}
          allNLPData={allNLPData}
          onClose={() => setIsOpen(false)}
          isExpanded={isExpanded}
          onToggleExpand={() => setIsExpanded(!isExpanded)}
        />
      )}
    </>
  );
}

export default ContextualAnalytics;
